/**
 * Excel Round-Trip Nuovocell — Dos documentos
 * --------------------------------------------
 *
 * 1. CATÁLOGO COMPLETO: todo menos Android (iPhone, Laptops, Tablets, Accesorios,
 *    Internet, Usados, Repuestos, Filamentos)
 *    Fórmula uniforme: ROUND(PVP × 1.14 × INTERNA / BCV, 0)
 *    Columna base: PVP TIENDA (campo Sanity: pvpTienda)
 *
 * 2. ANDROID: solo categoría smartphones
 *    Fórmula: ROUND(IF(COSTO < 100, COSTO × 1.44, COSTO × 1.34) × INTERNA / BCV, 0)
 *    Columna base: COSTO (campo Sanity: costoUSD)
 */

import { generarSlug, inferirMarca } from './excelParser';

const MARCAS_VALIDAS = ['Apple', 'Samsung', 'Xiaomi / Redmi', 'Honor', 'Infinix', 'Tecno', 'Itel', 'Huawei', 'Dell', 'HP', 'Lenovo', 'Otro'];
const CATEGORIAS_VALIDAS = ['iphone', 'smartphones', 'laptops', 'accesorios', 'tablets', 'internet', 'usados', 'repuestos', 'filamentos'];

// Etiquetas visibles
export const CATEGORIA_LABEL = {
  iphone:      'iPhone',
  smartphones: 'Smartphones (Android)',
  laptops:     'Laptops',
  accesorios:  'Accesorios',
  tablets:     'Tablets',
  internet:    'Internet Portátil',
  usados:      'Usados Certificados',
  repuestos:   'Repuestos',
  filamentos:  'Filamentos',
};

export { CATEGORIAS_VALIDAS };

// La categoría Android va en su propio Excel; el resto va al "catálogo completo"
const ES_ANDROID = (cat) => cat === 'smartphones';

// Tipo de Excel
export const TIPO_COMPLETO = 'completo'; // todo menos Android
export const TIPO_ANDROID  = 'android';

// Valores hipotéticos para columna base cuando no hay dato
const PVP_DEFAULT   = 100;
const COSTO_DEFAULT = 80;

// ============== GENERAR ==============

/**
 * Genera el Excel del tipo solicitado.
 * @param {Array} productos - todos los productos del catálogo
 * @param {Object} opts
 *   - tipo: 'completo' | 'android' (obligatorio)
 *   - tasaInterna, tasaBCV: para mostrar en el Excel y calcular fallback
 */
export function generarExcelCatalogo(productos, opts = {}) {
  const XLSX = window.XLSX;
  if (!XLSX) throw new Error('SheetJS no cargado');

  const { tipo = TIPO_COMPLETO, tasaInterna, tasaBCV } = opts;
  if (tipo !== TIPO_COMPLETO && tipo !== TIPO_ANDROID) {
    throw new Error(`Tipo de Excel inválido: ${tipo}`);
  }

  const interna = (tasaInterna && tasaInterna > 0) ? tasaInterna : 700;
  const bcv     = (tasaBCV && tasaBCV > 0) ? tasaBCV : 520.91;

  const esAndroid = (tipo === TIPO_ANDROID);

  // Filtrar productos
  const lista = esAndroid
    ? productos.filter(p => ES_ANDROID(p.categoria))
    : productos.filter(p => !ES_ANDROID(p.categoria));

  // Columnas
  const headerCosto = esAndroid ? 'COSTO' : 'PVP TIENDA';
  const cols = [
    { key: '_id',        header: 'ID (no editar)' },
    { key: 'nombre',     header: 'Nombre' },
  ];
  if (!esAndroid) {
    // En completo necesitamos saber la categoría
    cols.push({ key: 'categoria', header: 'Categoría' });
  }
  cols.push(
    { key: 'base',       header: headerCosto },       // pvpTienda o costoUSD según tipo
    { key: 'precio',     header: 'Precio (USD)' },     // con fórmula
    { key: 'disponible', header: 'Disponible' },
    { key: 'interna',    header: 'INTERNA' },
    { key: 'bcv',        header: 'BCV' },
  );

  const widths = esAndroid
    ? [22, 42, 12, 14, 12, 10, 10]
    : [22, 42, 22, 12, 14, 12, 10, 10];

  // Localizar índices de columna para la fórmula
  const baseColIdx    = cols.findIndex(c => c.key === 'base');
  const precioColIdx  = cols.findIndex(c => c.key === 'precio');
  const internaColIdx = cols.findIndex(c => c.key === 'interna');
  const bcvColIdx     = cols.findIndex(c => c.key === 'bcv');
  const baseLetter    = String.fromCharCode(65 + baseColIdx);
  const internaLetter = String.fromCharCode(65 + internaColIdx);
  const bcvLetter     = String.fromCharCode(65 + bcvColIdx);

  // Headers
  const headers = cols.map(c => c.header);

  // Filas
  const dataRows = lista.map((p, i) => {
    const fila = 2 + i;

    // Valor base: para Android = costoUSD, para Completo = pvpTienda
    let baseValor;
    if (esAndroid) {
      baseValor = (p.costoUSD != null && p.costoUSD > 0)
        ? p.costoUSD
        : COSTO_DEFAULT;
    } else {
      baseValor = (p.pvpTienda != null && p.pvpTienda > 0)
        ? p.pvpTienda
        : PVP_DEFAULT;
    }

    // Fórmula del precio
    let precioCell;
    if (esAndroid) {
      // ROUND(IF(C<100, C*1.44, C*1.34) * INTERNA/BCV, 0)
      precioCell = {
        t: 'n',
        f: `ROUND(IF(${baseLetter}${fila}<100, ${baseLetter}${fila}*1.44, ${baseLetter}${fila}*1.34)*$${internaLetter}$2/$${bcvLetter}$2, 0)`,
      };
    } else {
      // ROUND(PVP * 1.14 * INTERNA/BCV, 0)
      precioCell = {
        t: 'n',
        f: `ROUND((${baseLetter}${fila}*1.14)*$${internaLetter}$2/$${bcvLetter}$2, 0)`,
      };
    }

    return cols.map(c => {
      if (c.key === '_id')        return p._id || '';
      if (c.key === 'nombre')     return p.nombre || '';
      if (c.key === 'categoria')  return p.categoria || '';
      if (c.key === 'base')       return baseValor;
      if (c.key === 'precio')     return precioCell;
      if (c.key === 'disponible') return p.disponible === false ? 'No' : 'Sí';
      if (c.key === 'interna')    return i === 0 ? interna : '';
      if (c.key === 'bcv')        return i === 0 ? bcv : '';
      return '';
    });
  });

  const aoa = [headers, ...dataRows];
  const ws = XLSX.utils.aoa_to_sheet(aoa);
  ws['!cols'] = widths.map(w => ({ wch: w }));
  ws['!freeze'] = { xSplit: 0, ySplit: 1 };

  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, 'Productos');

  // Pestaña de instrucciones
  const titulo = esAndroid ? 'INSTRUCCIONES — Smartphones Android' : 'INSTRUCCIONES — Catálogo completo';
  const baseName = esAndroid ? 'COSTO' : 'PVP TIENDA';
  const formulaTxt = esAndroid
    ? '"Precio (USD)" se calcula: si COSTO < $100 → COSTO × 1.44, si no → COSTO × 1.34, todo × INTERNA / BCV.'
    : '"Precio (USD)" se calcula: PVP TIENDA × 1.14 × INTERNA / BCV.';

  const ayuda = [
    [titulo],
    [''],
    ['CÓMO EDITAR:'],
    ['  1. NO modifiques la columna "ID (no editar)".'],
    ['  2. Para AGREGAR un producto nuevo: agrega una fila al final, deja el ID vacío.'],
    [`  3. Llena el ${baseName} y los demás campos. El precio se calcula solo.`],
    ['  4. Para OCULTAR un producto: pon "No" en Disponible (o borra la fila).'],
    [''],
    ['CÓMO FUNCIONA EL PRECIO:'],
    [`  - "${baseName}" es el costo/precio base en USD.`],
    [`  - ${formulaTxt}`],
    ['  - INTERNA y BCV vienen del sistema (admin Nuovocell).'],
    [''],
  ];

  if (esAndroid) {
    ayuda.push(
      ['Este Excel contiene SOLO smartphones Android (no iPhone).'],
      ['Los productos nuevos se crean en categoría "smartphones".'],
      [''],
    );
  } else {
    ayuda.push(
      ['Este Excel contiene TODO el catálogo MENOS los Android (que tienen su propio Excel).'],
      ['Cuando agregues un producto nuevo, escribe su categoría en la columna "Categoría".'],
      [''],
      ['CATEGORÍAS VÁLIDAS:'],
      ...CATEGORIAS_VALIDAS.filter(c => c !== 'smartphones').map(c => ['  ' + c]),
      [''],
    );
  }

  ayuda.push(
    ['DISPONIBLE:  Sí = aparece en la web  |  No = oculto'],
    [''],
    ['Si te equivocas, los productos solo se ocultan (no se borran). El equipo web puede añadir imágenes en Sanity después.'],
  );

  const wsAyuda = XLSX.utils.aoa_to_sheet(ayuda);
  wsAyuda['!cols'] = [{ wch: 90 }];
  XLSX.utils.book_append_sheet(wb, wsAyuda, 'Instrucciones');

  return wb;
}

export function descargarWorkbook(wb, filename = 'catalogo-nuovocell.xlsx') {
  window.XLSX.writeFile(wb, filename);
}

// ============== PARSER ==============

export function parseExcelCatalogo(wb) {
  const XLSX = window.XLSX;
  if (!XLSX) throw new Error('SheetJS no cargado');

  const sheetName = wb.SheetNames.includes('Productos') ? 'Productos' : wb.SheetNames[0];
  const ws = wb.Sheets[sheetName];
  if (!ws) throw new Error('No se encontró ninguna hoja válida en el Excel');

  const aoa = XLSX.utils.sheet_to_json(ws, { header: 1, defval: null, raw: true });
  if (aoa.length < 1) throw new Error('El Excel está vacío');

  // Detectar fila de encabezados
  let headerRowIdx = 0;
  for (let i = 0; i < Math.min(aoa.length, 5); i++) {
    const row = (aoa[i] || []).map(c => String(c || '').toLowerCase().trim());
    if (row.some(c => c.startsWith('id')) || row.some(c => c.startsWith('nombre'))) {
      headerRowIdx = i;
      break;
    }
  }

  const headers = aoa[headerRowIdx].map(h => String(h || '').trim());

  // Mapear columnas
  const colMap = {
    _id:        ['id', 'idnoeditar'],
    nombre:     ['nombre'],
    categoria:  ['categoria'],
    pvpTienda:  ['pvptienda', 'pvp'],
    costoUSD:   ['costo'],
    precio:     ['precio', 'preciousd'],
    disponible: ['disponible'],
    interna:    ['interna'],
    bcv:        ['bcv'],
  };
  const colIdx = {};
  for (const [key, aliases] of Object.entries(colMap)) {
    const idx = headers.findIndex(h => {
      const n = normalizeHeader(h);
      return aliases.some(a => n === a || n.includes(a) && a.length >= 4);
    });
    if (idx !== -1) colIdx[key] = idx;
  }

  // Determinar tipo de Excel
  // - Si tiene columna COSTO y no PVP TIENDA → Android
  // - Si tiene PVP TIENDA → Completo
  const tieneCosto = colIdx.costoUSD !== undefined;
  const tienePVP   = colIdx.pvpTienda !== undefined;
  const tipo = (tieneCosto && !tienePVP) ? TIPO_ANDROID : TIPO_COMPLETO;

  const errores = [];
  if (colIdx.nombre === undefined) errores.push('Falta columna "Nombre"');

  // Leer INTERNA y BCV del Excel
  let internaExcel = null, bcvExcel = null;
  if (colIdx.interna !== undefined && aoa[headerRowIdx + 1]) {
    const v = aoa[headerRowIdx + 1][colIdx.interna];
    if (typeof v === 'number' && v > 0) internaExcel = v;
  }
  if (colIdx.bcv !== undefined && aoa[headerRowIdx + 1]) {
    const v = aoa[headerRowIdx + 1][colIdx.bcv];
    if (typeof v === 'number' && v > 0) bcvExcel = v;
  }

  const rows = [];
  for (let i = headerRowIdx + 1; i < aoa.length; i++) {
    const raw = aoa[i];
    if (!raw || !raw.some(c => c !== null && c !== undefined && String(c).trim() !== '')) continue;

    const idVal  = colIdx._id    !== undefined ? raw[colIdx._id]    : null;
    const nomVal = colIdx.nombre !== undefined ? raw[colIdx.nombre] : null;
    const sinId     = !idVal  || String(idVal).trim() === '';
    const sinNombre = !nomVal || String(nomVal).trim() === '';
    if (sinId && sinNombre) continue;

    const r = {};
    r._id        = colIdx._id        !== undefined ? safeStr(raw[colIdx._id]) : null;
    r.nombre     = colIdx.nombre     !== undefined ? safeStr(raw[colIdx.nombre]) : null;
    r.categoria  = colIdx.categoria  !== undefined ? safeStr(raw[colIdx.categoria]) : null;
    r.pvpTienda  = colIdx.pvpTienda  !== undefined ? parseNumeroFlexible(raw[colIdx.pvpTienda]) : null;
    r.costoUSD   = colIdx.costoUSD   !== undefined ? parseNumeroFlexible(raw[colIdx.costoUSD]) : null;
    r.precio     = colIdx.precio     !== undefined ? parseNumeroFlexible(raw[colIdx.precio]) : null;
    r.disponible = colIdx.disponible !== undefined ? parseBool(raw[colIdx.disponible]) : null;

    // FALLBACK: si precio viene null/0 pero tenemos base + tasas, calcular nosotros
    if ((r.precio == null || r.precio === 0) && internaExcel && bcvExcel) {
      if (tipo === TIPO_ANDROID && r.costoUSD && r.costoUSD > 0) {
        const margen = r.costoUSD < 100 ? 1.44 : 1.34;
        r.precio = Math.round(r.costoUSD * margen * internaExcel / bcvExcel);
      } else if (tipo === TIPO_COMPLETO && r.pvpTienda && r.pvpTienda > 0) {
        r.precio = Math.round(r.pvpTienda * 1.14 * internaExcel / bcvExcel);
      }
    }

    r._rowIndex = i + 1;
    rows.push(r);
  }

  return { rows, headers, colIdx, errores, tipo, internaExcel, bcvExcel };
}

function normalizeHeader(s) {
  return String(s)
    .normalize('NFD').replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .replace(/[*()_\-\s]/g, '')
    .trim();
}

function safeStr(v) {
  if (v === null || v === undefined || v === '') return null;
  return String(v).trim();
}

function parseBool(v) {
  if (v === null || v === undefined || v === '') return null;
  if (v === true) return true;
  if (v === false) return false;
  const s = String(v).toLowerCase().trim();
  if (['sí', 'si', 'true', 's', 'yes', 'y', '1', 'x'].includes(s)) return true;
  if (['no', 'false', 'n', '0'].includes(s)) return false;
  return null;
}

/**
 * Parsea números flexible: 1234.56, 1234,56, "$ 1.234,56", "$199"
 */
export function parseNumeroFlexible(v) {
  if (v === null || v === undefined || v === '') return null;
  if (typeof v === 'number') return isFinite(v) ? v : null;

  let s = String(v).trim();
  if (!s) return null;
  s = s.replace(/[$\s]/g, '').replace(/usd/gi, '');
  if (!s) return null;

  const hasComma = s.includes(',');
  const hasDot = s.includes('.');

  if (hasComma && hasDot) {
    if (s.lastIndexOf(',') > s.lastIndexOf('.')) {
      s = s.replace(/\./g, '').replace(',', '.');
    } else {
      s = s.replace(/,/g, '');
    }
  } else if (hasComma) {
    const partes = s.split(',');
    if (partes.length === 2 && partes[1].length === 2) {
      s = partes[0] + '.' + partes[1];
    } else {
      s = s.replace(/,/g, '');
    }
  }

  const n = parseFloat(s);
  return isFinite(n) ? n : null;
}

// ============== ANALIZAR ==============

/**
 * Compara Excel parseado con catálogo Sanity.
 * @param {Array} rowsExcel
 * @param {Array} productosSanity
 * @param {Object} opts - { tipo: 'completo' | 'android' }
 */
export function analizarCambios(rowsExcel, productosSanity, opts = {}) {
  const { tipo } = opts;
  if (!tipo) throw new Error('analizarCambios requiere tipo en opts');

  const sanityIdx = new Map();
  for (const p of productosSanity) sanityIdx.set(p._id, p);

  const idsEnExcel = new Set();
  const creates = [];
  const updates = [];
  const errores = [];

  // SAFEGUARD: IDs cambiados a 1,2,3...
  const idsExcel = rowsExcel.map(r => r._id).filter(Boolean);
  const matcheanAlgo = idsExcel.some(id => sanityIdx.has(String(id)));
  const todosSecuenciales = idsExcel.length > 5 && idsExcel.every(id => /^\d{1,5}$/.test(String(id).trim()));
  if (idsExcel.length > 0 && !matcheanAlgo && todosSecuenciales) {
    errores.push({
      fila: 0,
      msg: 'Los IDs del Excel fueron cambiados a números (1, 2, 3...). NO modifiques esa columna. Vuelve a descargar el Excel y edita sin tocar los IDs.'
    });
    return { creates: [], updates: [], faltantes: [], errores };
  }

  for (const r of rowsExcel) {
    if (!r.nombre || String(r.nombre).trim() === '') {
      errores.push({ fila: r._rowIndex, msg: 'Falta nombre' });
      continue;
    }

    // Determinar categoría
    let cat;
    if (tipo === TIPO_ANDROID) {
      cat = 'smartphones';
    } else {
      // Completo: viene del Excel o se infiere del producto existente
      if (r.categoria) {
        const c = String(r.categoria).trim().toLowerCase();
        if (!CATEGORIAS_VALIDAS.includes(c)) {
          errores.push({ fila: r._rowIndex, msg: `Categoría inválida "${r.categoria}" en "${r.nombre}". Válidas: ${CATEGORIAS_VALIDAS.filter(x => x !== 'smartphones').join(', ')}` });
          continue;
        }
        if (c === 'smartphones') {
          errores.push({ fila: r._rowIndex, msg: `"${r.nombre}" tiene categoría 'smartphones' pero los Android van en su propio Excel.` });
          continue;
        }
        cat = c;
      } else if (r._id && sanityIdx.has(String(r._id))) {
        cat = sanityIdx.get(String(r._id)).categoria;
      } else {
        errores.push({ fila: r._rowIndex, msg: `Falta categoría en producto nuevo "${r.nombre}"` });
        continue;
      }
    }

    const tienePrecio = r.precio != null && r.precio > 0;

    if (r._id && sanityIdx.has(String(r._id))) {
      // UPDATE
      const id = String(r._id);
      idsEnExcel.add(id);
      const actual = sanityIdx.get(id);

      // SAFEGUARD: el ID debe corresponder al tipo
      const esAndroidActual = ES_ANDROID(actual.categoria);
      if (tipo === TIPO_ANDROID && !esAndroidActual) {
        errores.push({
          fila: r._rowIndex,
          msg: `"${actual.nombre}" no es un Smartphone Android. No se puede actualizar desde este Excel.`
        });
        continue;
      }
      if (tipo === TIPO_COMPLETO && esAndroidActual) {
        errores.push({
          fila: r._rowIndex,
          msg: `"${actual.nombre}" es Smartphone Android. Usa el Excel de Android para actualizarlo.`
        });
        continue;
      }

      const sets = {};
      const unsets = [];
      const cambios = {};

      if (r.nombre !== actual.nombre) { sets.nombre = r.nombre; cambios.nombre = true; }
      if (cat !== actual.categoria)   { sets.categoria = cat; cambios.categoria = true; }

      // Base: pvpTienda (completo) o costoUSD (android)
      if (tipo === TIPO_ANDROID) {
        if (r.costoUSD != null && r.costoUSD > 0 && round2(r.costoUSD) !== round2(actual.costoUSD)) {
          sets.costoUSD = round2(r.costoUSD);
          cambios.costoUSD = true;
        }
      } else {
        if (r.pvpTienda != null && r.pvpTienda > 0 && round2(r.pvpTienda) !== round2(actual.pvpTienda)) {
          sets.pvpTienda = round2(r.pvpTienda);
          cambios.pvpTienda = true;
        }
      }

      // Precio
      if (tienePrecio) {
        if (round2(r.precio) !== round2(actual.precio)) {
          sets.precio = round2(r.precio);
          cambios.precio = true;
        }
      } else if (actual.precio) {
        unsets.push('precio');
        cambios.precio = true;
      }

      // Disponible
      const nuevoDisp = r.disponible === null ? true : !!r.disponible;
      const actualDisp = actual.disponible === undefined ? true : !!actual.disponible;
      if (nuevoDisp !== actualDisp) {
        sets.disponible = nuevoDisp;
        cambios.disponible = true;
      }

      if (Object.keys(cambios).length > 0) {
        updates.push({
          _id: id,
          nombreActual: actual.nombre,
          fila: r._rowIndex,
          sets, unsets, cambios,
        });
      }
    } else {
      // CREATE
      const slug = generarSlug(r.nombre);
      if (!slug) {
        errores.push({ fila: r._rowIndex, msg: `No se pudo generar slug para "${r.nombre}"` });
        continue;
      }
      const marca = inferirMarca(r.nombre);

      const doc = {
        _type: 'producto',
        nombre: r.nombre,
        slug: { _type: 'slug', current: slug },
        categoria: cat,
      };
      if (marca && MARCAS_VALIDAS.includes(marca)) doc.marca = marca;
      if (tipo === TIPO_ANDROID && r.costoUSD != null && r.costoUSD > 0) doc.costoUSD = round2(r.costoUSD);
      if (tipo === TIPO_COMPLETO && r.pvpTienda != null && r.pvpTienda > 0) doc.pvpTienda = round2(r.pvpTienda);
      if (tienePrecio) doc.precio = round2(r.precio);
      doc.disponible = r.disponible === null ? true : !!r.disponible;

      creates.push({ fila: r._rowIndex, doc });
    }
  }

  // FALTANTES: solo dentro del universo del tipo
  const faltantes = [];
  const universo = tipo === TIPO_ANDROID
    ? productosSanity.filter(p => ES_ANDROID(p.categoria))
    : productosSanity.filter(p => !ES_ANDROID(p.categoria));

  for (const p of universo) {
    if (!idsEnExcel.has(p._id) && p.disponible !== false) {
      faltantes.push({ _id: p._id, nombre: p.nombre });
    }
  }

  return { creates, updates, faltantes, errores };
}

// ============== utils ==============

function round2(n) {
  if (n === null || n === undefined) return n;
  return Math.round(Number(n) * 100) / 100;
}
